import { Controller } from "@hotwired/stimulus"
import {
  buildTooltipFor,
  hideAllTooltips,
  interactiveTooltip,
  positionTooltip,
} from "../utils/plantafel_tooltip"

// ONE delegated hover-tooltip controller for a whole capacity-planning surface (grid board,
// timeline, employees board).
//
// Same rationale as `planning-board-drop` (see its header comment): a dense board renders
// thousands of chips / bars / aggregated period cells, and giving each its own
// `plantafel-tooltip` Stimulus instance (+ 2 listeners) is the pattern that once froze the
// page for ~30s. The nodes now carry only the `data-plantafel-tooltip-data-value` payload;
// this controller resolves the hovered node via `closest` and renders through the shared
// utils/plantafel_tooltip module, so the markup stays identical to the per-node controller
// that Plantafel proper keeps (it also needs the legacy inline mode this delegate does not).
//
// mouseenter/mouseleave do not bubble, so delegation uses mouseover/mouseout with
// relatedTarget guards — the pair fires per child crossing, the guards collapse that back
// to enter/leave semantics for the resolved node.
//
// A tooltip holding a link (a dispatched element's task) must be reachable: leaving the node
// starts a short grace period instead of hiding, so the pointer can cross the gap into the
// tooltip; entering it cancels the hide, leaving it (anywhere but back onto the node) hides.
export default class extends Controller {
  static SELECTOR = "[data-plantafel-tooltip-data-value]"
  static HIDE_GRACE_MS = 200

  connect() {
    this.hovered = null
    this.tooltip = null
    this.showTimeout = null
    this.hideTimeout = null
    this.onOver = this.handleOver.bind(this)
    this.onOut = this.handleOut.bind(this)
    this.element.addEventListener("mouseover", this.onOver)
    this.element.addEventListener("mouseout", this.onOut)
  }

  disconnect() {
    this.element.removeEventListener("mouseover", this.onOver)
    this.element.removeEventListener("mouseout", this.onOut)
    this.hide()
  }

  handleOver(event) {
    // A node removed under the cursor (a chip's x, an undo, a drop replacing the cell) fires no
    // mouseout, so the tooltip of the node we were on would hang around detached. The per-node
    // controller this replaces cleaned up in disconnect(); this is the delegated equivalent.
    if (this.hovered && !this.hovered.isConnected) this.hide()

    const target = event.target.closest?.(this.constructor.SELECTOR)
    if (!target || !this.element.contains(target)) return
    // Crossing between children of the node already hovered — not a new enter (or back onto it
    // from its own interactive tooltip, which keeps it open).
    if (target === this.hovered) {
      this.cancelPendingHide()
      return
    }

    this.hide()
    this.hovered = target
    // Small delay to debounce sweeping the pointer across a row of chips.
    this.showTimeout = setTimeout(() => this.show(target), 150)
  }

  handleOut(event) {
    if (!this.hovered) return
    // Moved onto another descendant of the hovered node — still inside it.
    if (event.relatedTarget && this.hovered.contains(event.relatedTarget)) return
    // A mouseout elsewhere on the board that never involved the hovered node.
    if (!this.hovered.contains(event.target)) return
    if (this.tooltip && interactiveTooltip(this.tooltip)) {
      if (event.relatedTarget && this.tooltip.contains(event.relatedTarget)) return
      this.scheduleHide()
      return
    }
    this.hide()
  }

  scheduleHide() {
    this.cancelPendingHide()
    this.hideTimeout = setTimeout(() => this.hide(), this.constructor.HIDE_GRACE_MS)
  }

  cancelPendingHide() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout)
      this.hideTimeout = null
    }
  }

  // The tooltip lives on <body>, outside this controller's element, so it gets its own pair.
  bindInteractive(tooltip) {
    tooltip.addEventListener("mouseenter", () => this.cancelPendingHide())
    tooltip.addEventListener("mouseleave", (event) => {
      if (this.tooltip !== tooltip) return
      if (event.relatedTarget && this.hovered?.contains(event.relatedTarget)) return
      this.hide()
    })
  }

  show(target) {
    this.showTimeout = null
    // The node may have been swapped out by a Turbo stream while the debounce ran.
    if (!target.isConnected || !target.matches(":hover")) return

    hideAllTooltips()
    this.tooltip = buildTooltipFor(target)
    if (!this.tooltip) return
    if (interactiveTooltip(this.tooltip)) this.bindInteractive(this.tooltip)
    positionTooltip(this.tooltip, target)
  }

  hide() {
    this.cancelPendingHide()
    if (this.showTimeout) {
      clearTimeout(this.showTimeout)
      this.showTimeout = null
    }
    // Built fresh per hover and fully removed here — cell refreshes replace nodes constantly,
    // so caching per node would leak orphaned tooltips on <body>.
    if (this.tooltip) {
      this.tooltip.remove()
      this.tooltip = null
    }
    this.hovered = null
  }
}
